import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate, Outlet } from 'react-router-dom';
import axios from '../../pages/components/customAxios';
import baseUrl from '../../base_url';
import GlobalSearchModal from '../common/GlobalSearchModal';
import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  Package,
  Boxes,
  Tag,
  CreditCard,
  Mail,
  Activity,
  Search,
  Bell,
  Plus,
  LogOut,
  ChevronDown,
  Menu,
  X,
  ExternalLink
} from 'lucide-react';

export const AdminLayout = ({ children }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [commandData, setCommandData] = useState(null);

  // Raw dataset for global search
  const [searchDatasets, setSearchDatasets] = useState({
    orders: [],
    products: [],
    customers: [],
    coupons: [],
    messages: []
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Keyboard shortcut for Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
    setIsNotificationsOpen(false);
    setIsQuickActionsOpen(false);
  }, [location.pathname]);

  // Fetch command center alerts & badge counters
  const fetchCounters = useCallback(async () => {
    try {
      const [cmdRes, ordRes, prodRes, custRes, cpnRes, msgRes] = await Promise.allSettled([
        axios.get(`${baseUrl}api/admin/command-center`),
        axios.get(`${baseUrl}api/admin/get-orders`),
        axios.get(`${baseUrl}api/products/data/all-products`),
        axios.get(`${baseUrl}api/admin/get-all-users`),
        axios.get(`${baseUrl}api/coupon/get-all`),
        axios.get(`${baseUrl}api/contact`)
      ]);

      if (cmdRes.status === 'fulfilled' && cmdRes.value?.data?.data) {
        setCommandData(cmdRes.value.data.data);
      }

      setSearchDatasets({
        orders: ordRes.status === 'fulfilled' ? (ordRes.value?.data?.data || []) : [],
        products: prodRes.status === 'fulfilled' ? (prodRes.value?.data?.data || []) : [],
        customers: custRes.status === 'fulfilled' ? (custRes.value?.data?.data || []) : [],
        coupons: cpnRes.status === 'fulfilled' ? (cpnRes.value?.data?.data || []) : [],
        messages: msgRes.status === 'fulfilled' ? (msgRes.value?.data?.data || []) : [],
      });
    } catch (err) {
      console.warn('Could not refresh admin counters:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchCounters();
  }, [fetchCounters]);

  const handleLogout = async () => {
    try {
      await axios.post(`${baseUrl}api/admin/logout`);
    } catch {
      // ignore
    } finally {
      localStorage.removeItem('adminToken');
      navigate('/');
    }
  };

  const totalOrdersCount = searchDatasets.orders?.length || 0;
  const dispatchOrdersCount = useMemo(() => {
    if (Array.isArray(searchDatasets.orders) && searchDatasets.orders.length > 0) {
      return searchDatasets.orders.filter((o) => {
        const s = (o.status || '').toLowerCase();
        return s === 'placed' || s === 'confirmed' || s === 'processing';
      }).length;
    }
    return commandData?.queues?.dispatch?.count || 0;
  }, [searchDatasets.orders, commandData]);

  const navSections = [
    {
      title: 'Control Center',
      items: [
        { label: 'Overview', path: '/dashboard', icon: LayoutDashboard }
      ]
    },
    {
      title: 'Commerce',
      items: [
        {
          label: 'Orders',
          path: '/orders',
          icon: ShoppingBag,
          badge: dispatchOrdersCount,
          badgeLabel: 'To Dispatch',
          badgeColor: 'bg-amber-500',
          badgeTooltip: `${dispatchOrdersCount} orders awaiting dispatch (${totalOrdersCount || 0} total lifetime orders)`
        },
        { label: 'Products', path: '/view-products', icon: Package },
        { label: 'Inventory', path: '/inventory', icon: Boxes, badge: (commandData?.queues?.outOfStock?.count || 0) + (commandData?.queues?.lowStock?.count || 0), badgeColor: 'bg-rose-500', badgeTooltip: 'Items with low or zero stock' },
        { label: 'Promotions', path: '/coupons', icon: Tag },
        { label: 'Customers', path: '/customers', icon: Users },
        { label: 'Payments', path: '/payments', icon: CreditCard }
      ]
    },
    {
      title: 'Support & System',
      items: [
        { label: 'Support Inbox', path: '/contacts', icon: Mail, badge: commandData?.queues?.messages?.count, badgeColor: 'bg-stone-500', badgeTooltip: 'Unread customer messages' },
        { label: 'System Health', path: '/system-health', icon: Activity }
      ]
    }
  ];

  const totalUrgentNotifications = (commandData?.summary?.criticalCount || 0) + (commandData?.summary?.urgentCount || 0);

  return (
    <div className="flex h-screen bg-stone-100/70 text-stone-900 font-sans antialiased overflow-hidden">
      {/* Mobile backdrop */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-stone-900/50 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-stone-900 text-stone-300 flex flex-col border-r border-stone-800 transition-transform duration-200 ease-in-out ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Brand Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-800/80 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white font-bold text-sm shadow-md">
              MO
            </div>
            <div>
              <div className="font-semibold text-stone-100 text-sm tracking-tight flex items-center gap-1.5">
                Mia Omorfia
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>
              <div className="text-[10px] text-stone-400 uppercase tracking-widest font-mono">Control Center</div>
            </div>
          </div>
          <button
            onClick={() => setIsMobileSidebarOpen(false)}
            className="p-1 rounded-md text-stone-400 hover:text-white lg:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Sections */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-5 scrollbar-thin scrollbar-thumb-stone-800">
          {navSections.map((sec, idx) => (
            <div key={idx}>
              <div className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-stone-400">
                {sec.title}
              </div>
              <div className="space-y-0.5">
                {sec.items.map((item, itemIdx) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={itemIdx}
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                          isActive
                            ? 'bg-amber-600/20 text-amber-300 font-semibold border-l-2 border-amber-500'
                            : 'text-stone-300 hover:bg-stone-800/60 hover:text-stone-100'
                        }`
                      }
                    >
                      <div className="flex items-center gap-2.5 truncate">
                        <Icon className="w-4 h-4 shrink-0 text-stone-400" />
                        <span className="truncate">{item.label}</span>
                      </div>
                      {Boolean(item.badge && item.badge > 0) && (
                        <span
                          title={item.badgeTooltip || `${item.badge} pending items`}
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white cursor-help ${
                            item.badgeColor || 'bg-stone-700'
                          }`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Sidebar Footer / Quick Store Link with bottom padding for dev tools */}
        <div className="p-3 pb-8 border-t border-stone-800/80 bg-stone-950/40 shrink-0">
          <a
            href="https://shop.miaomorfia.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-3 py-2 rounded-lg text-xs text-stone-400 hover:text-stone-200 hover:bg-stone-800/50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              Live Storefront
            </span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="h-14 bg-white border-b border-stone-200 flex items-center justify-between px-4 sm:px-6 z-30 shrink-0 shadow-xs">
          {/* Left: Mobile trigger & Global Search Bar */}
          <div className="flex items-center gap-3 flex-1 max-w-xl">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="p-1.5 rounded-lg text-stone-600 hover:bg-stone-100 lg:hidden"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Global Search Trigger */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="w-full max-w-md flex items-center justify-between px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-stone-400 hover:border-stone-300 hover:bg-stone-100/70 transition-all text-xs text-left"
            >
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-stone-400 shrink-0" />
                <span className="truncate">Search orders, SKUs, customers...</span>
              </div>
              <kbd className="hidden sm:inline-block text-[10px] font-mono bg-white border border-stone-200 px-1.5 py-0.5 rounded text-stone-500 shadow-2xs">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Right: Quick Actions, Alerts, Admin Account */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Quick Actions Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsQuickActionsOpen((prev) => !prev)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-900 hover:bg-stone-800 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Action</span>
                <ChevronDown className="w-3 h-3 text-stone-400" />
              </button>

              {isQuickActionsOpen && (
                <div
                  className="absolute right-0 mt-1.5 w-48 bg-white border border-stone-200 rounded-xl shadow-xl py-1.5 z-40 text-xs font-medium text-stone-700 animate-in fade-in zoom-in-95 duration-100"
                  onClick={() => setIsQuickActionsOpen(false)}
                >
                  <button
                    onClick={() => navigate('/add-products')}
                    className="w-full text-left px-3.5 py-2 hover:bg-stone-50 flex items-center gap-2"
                  >
                    <Package className="w-3.5 h-3.5 text-stone-500" />
                    Add Product
                  </button>
                  <button
                    onClick={() => navigate('/coupons')}
                    className="w-full text-left px-3.5 py-2 hover:bg-stone-50 flex items-center gap-2"
                  >
                    <Tag className="w-3.5 h-3.5 text-stone-500" />
                    Create Promotion
                  </button>
                  <div className="my-1 border-t border-stone-100" />
                  <button
                    onClick={() => navigate('/orders?tab=ready_to_dispatch')}
                    className="w-full text-left px-3.5 py-2 hover:bg-amber-50 text-amber-900 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <ShoppingBag className="w-3.5 h-3.5 text-amber-600" />
                      Dispatch Queue
                    </span>
                    <span className="font-bold text-amber-700">
                      {commandData?.queues?.dispatch?.count || 0}
                    </span>
                  </button>
                </div>
              )}
            </div>

            {/* Notifications Bell with Operational Drawer */}
            <div className="relative">
              <button
                onClick={() => setIsNotificationsOpen((prev) => !prev)}
                className="relative p-2 rounded-lg text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
                title="Operational Alerts"
              >
                <Bell className="w-4 h-4" />
                {totalUrgentNotifications > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white" />
                )}
              </button>

              {isNotificationsOpen && (
                <div
                  className="absolute right-0 mt-1.5 w-80 sm:w-96 bg-white border border-stone-200 rounded-xl shadow-2xl py-2 z-40 text-xs text-stone-800 animate-in fade-in zoom-in-95 duration-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between px-4 py-2 border-b border-stone-100">
                    <span className="font-semibold text-stone-900 text-sm">Operational Alerts</span>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="text-amber-700 hover:underline text-[11px] font-semibold"
                    >
                      View Dashboard →
                    </button>
                  </div>

                  <div className="max-h-80 overflow-y-auto p-2 space-y-2">
                    {/* Orders Needing Dispatch */}
                    {(commandData?.queues?.dispatch?.count || 0) > 0 && (
                      <div
                        onClick={() => {
                          setIsNotificationsOpen(false);
                          navigate('/orders?tab=ready_to_dispatch');
                        }}
                        className="p-2.5 rounded-lg bg-amber-50/70 border border-amber-200/80 hover:bg-amber-100/80 cursor-pointer transition-colors"
                      >
                        <div className="font-semibold text-amber-900 flex items-center justify-between">
                          <span>Orders Ready for Dispatch</span>
                          <span className="px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[10px]">
                            {commandData.queues.dispatch.count}
                          </span>
                        </div>
                        <p className="text-[11px] text-amber-700 mt-0.5">
                          Paid customer orders awaiting courier packaging or Sendcloud label generation.
                        </p>
                      </div>
                    )}

                    {/* Stuck Payment Orders */}
                    {(commandData?.queues?.stuckPayment?.count || 0) > 0 && (
                      <div
                        onClick={() => {
                          setIsNotificationsOpen(false);
                          navigate('/orders?tab=pending');
                        }}
                        className="p-2.5 rounded-lg bg-rose-50/70 border border-rose-200/80 hover:bg-rose-100/80 cursor-pointer transition-colors"
                      >
                        <div className="font-semibold text-rose-900 flex items-center justify-between">
                          <span>Stuck Incomplete Payments</span>
                          <span className="px-1.5 py-0.5 rounded bg-rose-200 text-rose-900 text-[10px]">
                            {commandData.queues.stuckPayment.count}
                          </span>
                        </div>
                        <p className="text-[11px] text-rose-700 mt-0.5">
                          Pending orders older than 24 hours requiring cancellation or payment sync.
                        </p>
                      </div>
                    )}


                    {/* Stock Alert */}
                    {((commandData?.queues?.outOfStock?.count || 0) > 0 || (commandData?.queues?.lowStock?.count || 0) > 0) && (
                      <div
                        onClick={() => {
                          setIsNotificationsOpen(false);
                          navigate('/inventory');
                        }}
                        className="p-2.5 rounded-lg bg-stone-100 border border-stone-200 hover:bg-stone-200/60 cursor-pointer transition-colors"
                      >
                        <div className="font-semibold text-stone-900 flex items-center justify-between">
                          <span>Stock Reorder Warning</span>
                          <span className="px-1.5 py-0.5 rounded bg-stone-300 text-stone-800 text-[10px]">
                            {(commandData?.queues?.outOfStock?.count || 0) + (commandData?.queues?.lowStock?.count || 0)}
                          </span>
                        </div>
                        <p className="text-[11px] text-stone-600 mt-0.5">
                          Products have depleted below safe reorder thresholds.
                        </p>
                      </div>
                    )}

                    {totalUrgentNotifications === 0 && (
                      <div className="py-8 text-center text-stone-400">
                        <Activity className="w-6 h-6 mx-auto mb-1 text-emerald-500 stroke-[1.5]" />
                        <p className="font-medium text-stone-700">All queues clear</p>
                        <p className="text-[11px] text-stone-400">No urgent operational bottlenecks detected.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Logout Action */}
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-stone-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
              title="Sign Out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Workspace Body */}
        <main className="flex-1 overflow-y-auto bg-stone-50/50 p-4 sm:p-6 lg:p-8">
          {children || <Outlet />}
        </main>
      </div>

      {/* Global Search Modal */}
      <GlobalSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        orders={searchDatasets.orders}
        products={searchDatasets.products}
        customers={searchDatasets.customers}
        coupons={searchDatasets.coupons}
        messages={searchDatasets.messages}
      />
    </div>
  );
};

export default AdminLayout;
